// ReleveInternePrintUtility.js
import React from 'react';
import axios from 'axios';
import { Modal, Spin, Button } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import ReactDOM from 'react-dom';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const API_URL = `${process.env.REACT_APP_API_BASE_URL}`;

const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

const formatDateWithTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const seconds = d.getSeconds().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};

const formatNumber = (num) => {
    const numValue = Number(num) || 0;
    return numValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatFullDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const dayName = days[d.getDay()];
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${dayName} ${day} ${month} ${year}`;
};

// Template for relevé interne
const releveInterneTemplate = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <title>Relevé Interne ( ne pas envoyer au client ! )</title>
    <style>
        @page {
            size: A5 landscape;
            margin: 1cm;
        }
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            font-size: 11px;
            line-height: 1.3;
        }
        .header {
            margin-bottom: 15px;
        }
        .company-name {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 5px;
            text-align: left;
        }
        .title {
            text-align: center;
            font-weight: bold;
            font-size: 16px;
            margin: 5px 0 10px 0;
        }
        .subtitle {
            text-align: center;
            font-size: 12px;
            margin-bottom: 5px;
        }
        .client-info-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
        }
        .client-name {
            font-weight: bold;
            font-size: 13px;
            text-align: left;
        }
        .date-info {
            text-align: right;
            font-size: 11px;
        }
        .page-info {
            position: absolute;
            top: 0;
            right: 0;
            font-size: 10px;
            padding: 5px;
        }
        .flags-info {
            position: absolute;
            top: 25px;
            right: 0;
            font-size: 9px;
            padding: 5px;
            text-align: right;
        }
        .header {
            margin-bottom: 15px;
            position: relative;
        }
        .conditions-section {
            margin-top: 5px;
            margin-bottom: 5px;
            font-size: 10px;
            line-height: 1.2;
        }
        .conditions-title {
            font-weight: bold;
            margin-bottom: 2px;
        }
        .conditions-content {
            text-align: left;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 10px;
        }
        table th, table td {
            border: none;
            padding: 4px;
            text-align: left;
        }
        table th {
            background-color: #f0f0f0;
            font-weight: bold;
            text-align: center;
            font-size: 10px;
        }
        table td {
            font-size: 11px;
        }
        /* Header row with bottom border only for visual separation */
        table thead tr {
            border-bottom: 2px solid #000;
        }
        /* Subtle separators between data rows */
        table tbody tr {
            border-bottom: 1px solid #f0f0f0;
        }
        .reference-col {
            width: 15%;
        }
        .remarque-col {
            width: 19%;
            font-size: 10px;
        }
        .date-col {
            width: 10%;
        }
        .modeliv-col {
            width: 10%;
            font-size: 9px;
        }
        .mode-paie-col {
            width: 10%;
            font-size: 9px;
        }
        .mnt-ttc-col {
            width: 10%;
            text-align: right;
        }
        .mnt-regler-col {
            width: 10%;
            text-align: right;
        }
        .solde-col {
            width: 14%;
            text-align: right;
        }
        .text-right {
            text-align: right;
        }
        .total-row {
            font-weight: bold;
            background-color: #e8e8e8;
        }
        .grand-total-row {
            font-weight: bold;
            background-color: #d0d0d0;
            font-size: 12px;
            border-top: 2px solid #000;
            border-bottom: none;
        }
        @media print {
            body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="page-info">Page {{PAGE_NUM}} sur {{TOTAL_PAGES}}</div>
        <div class="flags-info">
            {{MAJ_M3}}<br>
            {{MAJ_SL}}
        </div>
        <div class="company-name">IHSSAN PIECES AUTO</div>
        <div class="title">Relevé de compte clients</div>
        <div class="subtitle">Relevé interne</div>
        <div class="client-info-row">
            <div class="client-name">{{CLIENT_NAME}}</div>
            <div class="date-info">{{CURRENT_DATE}}</div>
        </div>
        {{CONDITIONS_SECTION}}
    </div>
    
    {{SECTIONS}}
</body>
</html>`;

const processTemplate = (template, replacements) => {
    let processedTemplate = template;
    Object.entries(replacements).forEach(([key, value]) => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        processedTemplate = processedTemplate.replace(regex, value || '');
    });
    return processedTemplate;
};

// Helper function to get DATEVALID from a record with fetched data, fallback to CreatedAt
const getDateValid = (record, factureData, avoirData) => {
    if (factureData && factureData.DATEVALID) {
        return factureData.DATEVALID;
    } else if (record.facture && record.facture.DATEVALID) {
        return record.facture.DATEVALID;
    } else if (avoirData && avoirData.DATEVALID) {
        return avoirData.DATEVALID;
    } else if (record.avoir && record.avoir.DATEVALID) {
        return record.avoir.DATEVALID;
    } else if (record.surplus && record.surplus.DATE_SURPLUS) {
        return record.surplus.DATE_SURPLUS;
    }
    // Fallback to CreatedAt if no DATEVALID found
    return record.CreatedAt || null;
};

const generateReleveSections = async (unifiedRecords, clientInfo) => {
    // Filter records where MNT_TTC != MNT_REGLER (not fully settled)
    const unpaidRecords = unifiedRecords.filter(record => {
        const mntTtc = record.MNT_TTC || 0;
        const mntRegler = record.MNT_REGLER || 0;
        // Check if not fully settled (accounting for negative values)
        return mntTtc !== mntRegler;
    });

    // Fetch facture and avoir details for all records in parallel
    const fetchPromises = unpaidRecords.map(async (record) => {
        let factureData = null;
        let avoirData = null;

        // Fetch facture if factureId exists
        if (record.factureId) {
            factureData = await fetchFactureById(record.factureId);
        }

        // Fetch avoir if avoirId exists
        if (record.avoirId) {
            avoirData = await fetchAvoirById(record.avoirId);
        }

        return { record, factureData, avoirData };
    });

    const recordsWithDetails = await Promise.all(fetchPromises);

    // Sort records by DATEVALID (oldest first)
    recordsWithDetails.sort((a, b) => {
        const dateA = getDateValid(a.record, a.factureData, a.avoirData);
        const dateB = getDateValid(b.record, b.factureData, b.avoirData);
        const dateAValue = dateA ? new Date(dateA) : new Date(0);
        const dateBValue = dateB ? new Date(dateB) : new Date(0);
        return dateAValue - dateBValue;
    });

    let rowsHtml = '';
    let grandTotalMntTtc = 0;
    let grandTotalMntRegler = 0;
    let grandTotalSolde = 0;

    // Generate rows for all records in one table
    recordsWithDetails.forEach(({ record, factureData, avoirData }) => {
        // Use raw values - avoir and surplus are already negative
        const mntTtc = record.MNT_TTC || 0;
        const mntRegler = record.MNT_REGLER || 0;
        const solde = mntTtc - mntRegler;

        grandTotalMntTtc += mntTtc;
        grandTotalMntRegler += mntRegler;
        grandTotalSolde += solde;

        // Get reference from related records
        let ref = record.REF_AV_FAC || '';
        let remarque = '';
        let modeliv = '';
        let modePaie = '';

        // Use fetched facture data if available, otherwise fall back to record.facture
        if (factureData) {
            ref = factureData.REF_FAC || ref;
            remarque = factureData.REMARQUE || '';
            modeliv = factureData.MODELIV || '';
            modePaie = factureData.MODE_PAIE || '';
        } else if (record.facture) {
            ref = record.facture.REF_FAC || ref;
            remarque = record.facture.REMARQUE || '';
            modeliv = record.facture.MODELIV || '';
            modePaie = record.facture.MODE_PAIE || '';
        } else if (avoirData) {
            ref = avoirData.REF_AVR || ref;
        } else if (record.avoir) {
            ref = record.avoir.REF_AVR || ref;
        } else if (record.surplus) {
            ref = record.surplus.REF_SURPLUS || ref;
        } else if (record.currentRef) {
            ref = record.currentRef;
        }

        // Use DATEVALID from related records
        const dateValid = getDateValid(record, factureData, avoirData);
        const date = formatDate(dateValid);

        rowsHtml += `
            <tr>
                <td class="reference-col">${ref}</td>
                <td class="remarque-col" style="font-size: 7px;">${remarque}</td>
                <td class="date-col">${date}</td>
                <td class="modeliv-col">${modeliv}</td>
                <td class="mode-paie-col">${modePaie}</td>
                <td class="mnt-ttc-col text-right">${formatNumber(mntTtc)}</td>
                <td class="mnt-regler-col text-right">${formatNumber(mntRegler)}</td>
                <td class="solde-col text-right">${formatNumber(solde)}</td>
            </tr>`;
    });

    // Generate single table with all records
    let sectionsHtml = '';
    
    if (unpaidRecords.length > 0) {
        sectionsHtml = `
            <table>
                <thead>
                    <tr>
                        <th class="reference-col">Référence</th>
                        <th class="remarque-col">Remarque</th>
                        <th class="date-col">Date</th>
                        <th class="modeliv-col">M. Liv</th>
                        <th class="mode-paie-col">M. Pai</th>
                        <th class="mnt-ttc-col">Mnt ttc</th>
                        <th class="mnt-regler-col">Mnt régler</th>
                        <th class="solde-col">Solde</th>
                    </tr>
                </thead>
                <tbody>
                    ${rowsHtml}
                    <tr class="grand-total-row">
                        <td colspan="5" style="text-align: right; font-weight: bold;">TOTAL :</td>
                        <td class="text-right">${formatNumber(grandTotalMntTtc)}</td>
                        <td class="text-right">${formatNumber(grandTotalMntRegler)}</td>
                        <td class="text-right">${formatNumber(grandTotalSolde)}</td>
                    </tr>
                </tbody>
            </table>
        `;
    }

    return sectionsHtml;
};

// Generate conditions section HTML
const generateConditionsSection = (clientInfo) => {
    const conditions = clientInfo?.conditions || '';
    if (!conditions) {
        return '';
    }
    
    return `
        <div class="conditions-section">
            <div class="conditions-content">${conditions}</div>
        </div>
    `;
};

// Fetch facture by ID
const fetchFactureById = async (factureId) => {
    try {
        if (!factureId) return null;
        const response = await axios.get(`${API_URL}/fa/getfacture/${factureId}`);
        return response.data || null;
    } catch (error) {
        // If facture doesn't exist, return null (don't throw error)
        console.warn(`Facture ${factureId} not found:`, error.message);
        return null;
    }
};

// Fetch avoir by ID
const fetchAvoirById = async (avoirId) => {
    try {
        if (!avoirId) return null;
        const response = await axios.get(`${API_URL}/av/avoir/${avoirId}`);
        return response.data || null;
    } catch (error) {
        // If avoir doesn't exist, return null (don't throw error)
        console.warn(`Avoir ${avoirId} not found:`, error.message);
        return null;
    }
};

// Fetch unified records for a client
const fetchUnifiedRecords = async (codeClt) => {
    try {
        const response = await axios.get(`${API_URL}/reglement/getAllUnified/${codeClt}`);
        return response.data || [];
    } catch (error) {
        console.error('Error fetching unified records:', error);
        throw new Error(`Failed to fetch unified records: ${error.message}`);
    }
};

// Fetch client info
const fetchClientInfo = async (codeClt) => {
    try {
        const response = await axios.get(`${API_URL}/client/getclientbycodeclt/${codeClt}`);
        return response.data || {};
    } catch (error) {
        console.error('Error fetching client info:', error);
        return {};
    }
};

// Fetch dbfCheck flags (SL and M3)
const fetchDbfCheckFlags = async () => {
    try {
        const response = await axios.get(`${API_URL}/reglement/dbfcheck/flags`);
        return response.data || { SL: null, M3: null };
    } catch (error) {
        console.error('Error fetching dbfCheck flags:', error);
        return { SL: null, M3: null };
    }
};

// Generate PDF blob
const generatePdfBlob = async (htmlString) => {
    const container = document.createElement('div');
    container.style.width = '210mm';
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.innerHTML = htmlString;
    document.body.appendChild(container);
    
    try {
        const pdf = new jsPDF('l', 'mm', 'a5');
        const margin = 10;
        
        const page = container;
        page.style.width = '210mm';
        page.style.height = '148mm';
        page.style.padding = `${margin}mm`;
        page.style.boxSizing = 'border-box';
        page.style.backgroundColor = '#FFFFFF';

        const canvas = await html2canvas(page, {
            scale: 3,
            useCORS: true,
            logging: false,
            letterRendering: true,
            backgroundColor: '#FFFFFF',
            width: page.offsetWidth,
            height: page.offsetHeight,
        });

        const imgData = canvas.toDataURL('image/jpeg', 1.0);
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 148);
        
        return pdf.output('blob');
    } finally {
        document.body.removeChild(container);
    }
};

// Main print function
export const printReleveInterne = async (facture) => {
    try {
        // Create loading modal
        const loadingModalDiv = document.createElement('div');
        document.body.appendChild(loadingModalDiv);
        
        const closeLoadingModal = () => {
            ReactDOM.unmountComponentAtNode(loadingModalDiv);
            document.body.removeChild(loadingModalDiv);
        };
        
        ReactDOM.render(
            React.createElement(Modal, {
                title: "Génération du PDF",
                open: true,
                closable: true,
                maskClosable: false,
                footer: [
                    React.createElement(Button, {
                        key: "exit",
                        onClick: closeLoadingModal
                    }, "Sortir")
                ]
            }, React.createElement('div', {
                style: { display: 'flex', justifyContent: 'center', padding: '2rem' }
            }, React.createElement(Spin, {
                indicator: React.createElement(LoadingOutlined, { style: { fontSize: 24 }, spin: true }),
                tip: "Préparation du document..."
            }))),
            loadingModalDiv
        );

        // Fetch data
        const codeClt = facture.CODE_CLT || facture.code_clt;
        if (!codeClt) {
            throw new Error('Code client manquant');
        }

        const [unifiedRecords, clientInfo, flags] = await Promise.all([
            fetchUnifiedRecords(codeClt),
            fetchClientInfo(codeClt),
            fetchDbfCheckFlags()
        ]);

        // Generate sections (now async)
        const sectionsHtml = await generateReleveSections(unifiedRecords, clientInfo);
        const conditionsSection = generateConditionsSection(clientInfo);
        
        // Current date
        const currentDate = formatFullDate(new Date());
        
        // Format client name and code for header
        const clientName = clientInfo?.CLIENT || '';
        const clientCode = clientInfo?.COMPTE ? ` (${clientInfo.COMPTE})` : '';
        const clientDisplay = clientName + clientCode;
        
        // Format flags dates with time
        const majM3 = flags.M3?.lastChecked 
            ? `majM3: ${formatDateWithTime(flags.M3.lastChecked)}` 
            : 'majM3: -';
        const majSL = flags.SL?.lastChecked 
            ? `majSL: ${formatDateWithTime(flags.SL.lastChecked)}` 
            : 'majSL: -';
        
        // Generate HTML
        const html = processTemplate(releveInterneTemplate, {
            CLIENT_NAME: clientDisplay,
            CURRENT_DATE: currentDate,
            CONDITIONS_SECTION: conditionsSection,
            SECTIONS: sectionsHtml,
            PAGE_NUM: '1',
            TOTAL_PAGES: '1',
            MAJ_M3: majM3,
            MAJ_SL: majSL
        });

        // Generate PDF
        const pdfBlob = await generatePdfBlob(html);
        
        // Close loading modal
        closeLoadingModal();

        // Create preview modal
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const previewModalDiv = document.createElement('div');
        document.body.appendChild(previewModalDiv);
        
        const closePreviewModal = () => {
            URL.revokeObjectURL(pdfUrl);
            ReactDOM.unmountComponentAtNode(previewModalDiv);
            document.body.removeChild(previewModalDiv);
        };
        
        ReactDOM.render(
            React.createElement(Modal, {
                title: `Aperçu PDF - Relevé Interne`,
                open: true,
                width: "90%",
                centered: true,
                closable: true,
                maskClosable: true,
                footer: [
                    React.createElement(Button, {
                        key: "exit",
                        onClick: closePreviewModal
                    }, "Sortir")
                ],
                onCancel: closePreviewModal
            }, React.createElement('div', {
                className: "pdf-preview-container"
            }, React.createElement('iframe', {
                src: pdfUrl,
                style: { width: '100%', height: '80vh', border: 'none' },
                title: "PDF Preview"
            }))),
            previewModalDiv
        );

        return {
            cleanup: closePreviewModal,
            pdfUrl
        };
    } catch (error) {
        console.error('Error generating relevé interne:', error);
        Modal.error({
            title: 'Erreur',
            content: `Échec de la génération du document: ${error.message}`,
        });
        throw new Error(`Failed to generate document: ${error.message}`);
    }
};

// Direct print function
export const printReleveInterneDirect = async (facture) => {
    try {
        // Fetch data
        const codeClt = facture.CODE_CLT || facture.code_clt;
        if (!codeClt) {
            throw new Error('Code client manquant');
        }

        const [unifiedRecords, clientInfo, flags] = await Promise.all([
            fetchUnifiedRecords(codeClt),
            fetchClientInfo(codeClt),
            fetchDbfCheckFlags()
        ]);

        // Generate sections (now async)
        const sectionsHtml = await generateReleveSections(unifiedRecords, clientInfo);
        const conditionsSection = generateConditionsSection(clientInfo);
        
        // Current date
        const currentDate = formatFullDate(new Date());
        
        // Format client name and code for header
        const clientName = clientInfo?.CLIENT || '';
        const clientCode = clientInfo?.COMPTE ? ` (${clientInfo.COMPTE})` : '';
        const clientDisplay = clientName + clientCode;
        
        // Format flags dates with time
        const majM3 = flags.M3?.lastChecked 
            ? `majM3: ${formatDateWithTime(flags.M3.lastChecked)}` 
            : 'majM3: -';
        const majSL = flags.SL?.lastChecked 
            ? `majSL: ${formatDateWithTime(flags.SL.lastChecked)}` 
            : 'majSL: -';
        
        // Generate HTML
        const html = processTemplate(releveInterneTemplate, {
            CLIENT_NAME: clientDisplay,
            CURRENT_DATE: currentDate,
            CONDITIONS_SECTION: conditionsSection,
            SECTIONS: sectionsHtml,
            PAGE_NUM: '1',
            TOTAL_PAGES: '1',
            MAJ_M3: majM3,
            MAJ_SL: majSL
        });

        // Create iframe for printing
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position: absolute; width: 0; height: 0; left: -9999px; border: 0;';
        document.body.appendChild(iframe);
        
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        doc.open();
        doc.write(html);
        doc.close();
        
        // Print after load
        iframe.onload = () => {
            setTimeout(() => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                    
                    setTimeout(() => {
                        document.body.removeChild(iframe);
                    }, 1000);
                } catch (err) {
                    console.error('Error in print process:', err);
                    document.body.removeChild(iframe);
                }
            }, 500);
        };
    } catch (error) {
        console.error('Error generating relevé interne for direct print:', error);
        throw new Error(`Failed to generate document: ${error.message}`);
    }
};

